import React from 'react';
import {useShallow} from 'zustand/react/shallow';
import {useReportStore} from '../../../useReportStore';
import {searchUnified} from './sourcesApi';
import {
    useSourcesExplorerActions,
    useSourcesExplorerDataState,
} from './useSourcesSectionStoreSelectors';
import {SEARCH_FETCH_LIMIT} from './useUnifiedSourcesSearch';

/**
 * documentStore values returned by the unified search API:
 *   'corpus' | 'data_item' | 'report' | 'highlight'
 *
 * We map these onto the scope-button values:
 *   'corpus'    → corpus
 *   'data_item' → data_items
 *   'report'    → reports
 *   'highlight' → highlights
 */
const STORE_TO_SCOPE = {
    corpus: 'corpus',
    data_item: 'data_items',
    report: 'reports',
    highlight: 'highlights',
};

function deriveCountsFromItems(items) {
    const segmentsByScope = {};
    const docsByScope = {};

    for (const hit of items) {
        const scope = STORE_TO_SCOPE[hit.documentStore];
        if (!scope) continue;

        if (!segmentsByScope[scope]) {
            segmentsByScope[scope] = 0;
            docsByScope[scope] = new Set();
        }

        segmentsByScope[scope] += 1;

        const docId = hit.openTarget?.docId
            ?? hit.openTarget?.canonicalFileId
            ?? hit.openTarget?.reportId
            ?? hit.openTarget?.dataItemId
            ?? null;
        if (docId != null) {
            docsByScope[scope].add(String(docId));
        }
    }

    const counts = {all: {docs: 0, segments: 0}};
    for (const scope of Object.keys(segmentsByScope)) {
        const docs = docsByScope[scope].size;
        const segments = segmentsByScope[scope];
        counts[scope] = {docs, segments};
        counts.all.docs += docs;
        counts.all.segments += segments;
    }

    return counts;
}

function useSearchCountFilters() {
    return useReportStore(
        useShallow((state) => ({
            unifiedSearchMode: state.unifiedSearchMode,
            searchPassageContext: state.searchPassageContext,
            docSource: state.docSource,
            subTypes: state.subTypes,
            tags: state.tags,
            classificationLevels: state.classificationLevels,
            productTypes: state.productTypes,
        }))
    );
}

/**
 * Fires a parallel background query with `sources=all` whenever the debounced query changes.
 * Writes per-scope document + segment counts into the explorer store so the scope toggle buttons
 * can display them without affecting the main scoped search results.
 */
export default function useSearchScopeCounts({userId, enabled = true}) {
    const {debouncedQuery} = useSourcesExplorerDataState();
    const {setScopeCounts, setIsScopeCounting} = useSourcesExplorerActions();
    const {
        unifiedSearchMode,
        searchPassageContext,
        docSource,
        subTypes,
        tags,
        classificationLevels,
        productTypes,
    } = useSearchCountFilters();

    const abortRef = React.useRef(null);

    React.useEffect(() => {
        if (!enabled) return undefined;

        const query = (debouncedQuery || '').trim();
        const hasTagFilter = Array.isArray(tags) && tags.length > 0;

        if (!query && !hasTagFilter) {
            if (abortRef.current) {
                abortRef.current.abort();
                abortRef.current = null;
            }
            setScopeCounts(null);
            return undefined;
        }

        const controller = new AbortController();
        if (abortRef.current) abortRef.current.abort();
        abortRef.current = controller;

        let cancelled = false;
        setIsScopeCounting(true);

        const documentTypes = subTypes && subTypes.length ? subTypes : undefined;
        const context = searchPassageContext ? searchPassageContext : undefined;

        searchUnified({
            q: query,
            userId,
            sources: 'all',
            mode: unifiedSearchMode || 'keyword',
            context,
            scope: 'all',
            uploadedBranch: 'all',
            documentTypes,
            docSource: docSource || undefined,
            tags,
            classificationLevels,
            productTypes: productTypes && productTypes.length ? productTypes : undefined,
            limit: SEARCH_FETCH_LIMIT,
            signal: controller.signal,
        })
            .then((result) => {
                if (cancelled || controller.signal.aborted) return;
                setScopeCounts(deriveCountsFromItems(result.items || []));
            })
            .catch((error) => {
                if (cancelled || controller.signal.aborted || error?.name === 'AbortError') return;
                setScopeCounts(null);
            })
            .finally(() => {
                if (!cancelled && !controller.signal.aborted) {
                    setIsScopeCounting(false);
                }
            });

        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [
        enabled,
        debouncedQuery,
        userId,
        unifiedSearchMode,
        searchPassageContext,
        docSource,
        subTypes,
        tags,
        classificationLevels,
        productTypes,
        setScopeCounts,
        setIsScopeCounting,
    ]);

    React.useEffect(() => () => {
        if (abortRef.current) abortRef.current.abort();
    }, []);
}
